import React from "react";
import "./App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { ThemeProvider } from "next-themes";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { EditModeProvider } from "./context/EditModeContext";
import { I18nProvider } from "./lib/i18n";
import ProtectedRoute from "./components/ProtectedRoute";
import DashboardLayout from "./components/DashboardLayout";
import ChatbotWidget from "./components/ChatbotWidget";
import { useEditModeSession } from "./hooks/useEditModeSession";

import AdminApp from "./pages/admin/AdminApp";
import Landing from "./pages/Landing";
import Login from "./pages/Login";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import Register from "./pages/Register";
import OtpVerify from "./pages/OtpVerify";
import Kyc from "./pages/Kyc";
import AcceptInvite from "./pages/AcceptInvite";
import Profile from "./pages/Profile";
import RentalDetail from "./pages/RentalDetail";
import AgreementsDashboard from "./pages/AgreementsDashboard";
import TopAgents from "./pages/TopAgents";
import FindAgent from "./pages/FindAgent";
import ForTenants from "./pages/ForTenants";
import ForLandlords from "./pages/ForLandlords";
import ForBrokers from "./pages/ForBrokers";
import Trust from "./pages/Trust";
import About from "./pages/About";
import Contact from "./pages/Contact";

import BrokerAdminHome from "./pages/broker_admin/BrokerAdminHome";
import Team from "./pages/broker_admin/Team";
import AllDeals from "./pages/broker_admin/AllDeals";
import Analytics from "./pages/broker_admin/Analytics";
import BusinessProfile from "./pages/broker_admin/BusinessProfile";
import Plan from "./pages/broker/Plan";
import NewRental from "./pages/broker/NewRental";

import AgentHome from "./pages/agent/AgentHome";
import Earnings from "./pages/agent/Earnings";
import BrokerEarnings from "./pages/broker_admin/Earnings";

import TenantHome from "./pages/tenant/TenantHome";
import TenantWallet from "./pages/tenant/TenantWallet";
import LandlordHome from "./pages/landlord/LandlordHome";
import LandlordDeposits from "./pages/landlord/LandlordDeposits";
import LandlordWallet from "./pages/landlord/LandlordWallet";
import LandlordNotifications from "./pages/landlord/LandlordNotifications";
import LandlordSupport from "./pages/landlord/LandlordSupport";
import Terms from "./pages/Terms";
import Privacy from "./pages/Privacy";
import PaymentStatus from "./pages/PaymentStatus";
import UpgradeToBroker from "./pages/UpgradeToBroker";
import TenantProfile from "./pages/tenant/TenantProfile";
import PublicProfile from "./pages/PublicProfile";
import Referral from "./pages/agent/Referral";
import Settings from "./pages/Settings";
import AgreementSigning from "./pages/AgreementSigning";
import WitnessSigning from "./pages/WitnessSigning";
import AgreementBuilder from "./pages/AgreementBuilder";

function HomeByRole() {
  const { user } = useAuth();
  const { isInEditMode } = useEditModeSession();

  // If in edit mode, use the mock user from sessionStorage
  let currentUser = user;
  if (isInEditMode) {
    const editModeUserStr = sessionStorage.getItem("editModeUser");
    if (editModeUserStr) {
      try {
        currentUser = JSON.parse(editModeUserStr);
      } catch (e) {
        console.error("Failed to parse mock user:", e);
      }
    }
  }

  // Don't redirect to /admin if in edit mode
  if (currentUser?.role === "orello_admin" && !isInEditMode) return <Navigate to="/admin" replace />;
  if (currentUser?.role === "broker_admin") return <BrokerAdminHome />;
  if (currentUser?.role === "agent") return <AgentHome />;
  if (currentUser?.role === "landlord") return <LandlordHome />;
  return <TenantHome />;
}

function EarningsByRole() {
  const { user } = useAuth();
  if (user?.role === "broker_admin") return <BrokerEarnings />;
  return <Earnings />;
}

function DealsByRole() {
  const { user } = useAuth();
  if (user?.role === "broker_admin") return <AllDeals />;
  if (user?.role === "agent") return <AllDeals scope="agent" />;
  if (user?.role === "landlord") return <LandlordHome />;
  return <TenantHome />;
}

function App() {
  return (
    <ThemeProvider attribute="class" defaultTheme="dark" enableSystem={false}>
    <I18nProvider>
      <AuthProvider>
        <EditModeProvider>
        <Toaster position="top-center" richColors />
        <BrowserRouter>
          <Routes>
            <Route path="/" element={<Landing />} />
            <Route path="/for-tenants" element={<ForTenants />} />
            <Route path="/for-landlords" element={<ForLandlords />} />
            <Route path="/for-brokers" element={<ForBrokers />} />
            <Route path="/trust" element={<Trust />} />
            <Route path="/about" element={<About />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/top-agents" element={<TopAgents />} />
            <Route path="/find-agent" element={<FindAgent />} />
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/verify-otp" element={<OtpVerify />} />
            <Route path="/onboarding/kyc" element={<ProtectedRoute><Kyc /></ProtectedRoute>} />
            <Route path="/invite/:token" element={<AcceptInvite />} />

            {/* E-Sign: Agreement signing via shareable link */}
            <Route path="/agreements/:agreement_id/sign" element={<ProtectedRoute><AgreementSigning /></ProtectedRoute>} />
            {/* Public - no account needed. Witnesses reach this via a single-use emailed link. */}
            <Route path="/agreements/witness-sign/:token" element={<WitnessSigning />} />

            <Route path="/app" element={<ProtectedRoute><DashboardLayout /></ProtectedRoute>}>
              <Route index element={<HomeByRole />} />
              <Route path="deals" element={<DealsByRole />} />
              <Route path="agreements" element={<AgreementsDashboard />} />
              {/* Agreement Builder: Create/Edit agreements with live preview (Brokers, Agents, Self-managed Landlords only) */}
              <Route path="agreement-builder" element={<ProtectedRoute roles={["broker_admin", "agent", "landlord"]}><AgreementBuilder /></ProtectedRoute>} />
              <Route path="new-rental" element={<ProtectedRoute roles={["agent", "broker_admin", "landlord"]}><NewRental /></ProtectedRoute>} />
              <Route path="deposits" element={<ProtectedRoute roles={["landlord"]}><LandlordDeposits /></ProtectedRoute>} />
              <Route path="wallet" element={<ProtectedRoute roles={["landlord"]}><LandlordWallet /></ProtectedRoute>} />
              <Route path="tenant-wallet" element={<ProtectedRoute roles={["tenant"]}><TenantWallet /></ProtectedRoute>} />
              <Route path="notifications" element={<ProtectedRoute roles={["landlord"]}><LandlordNotifications /></ProtectedRoute>} />
              <Route path="support" element={<ProtectedRoute roles={["landlord"]}><LandlordSupport /></ProtectedRoute>} />
              <Route path="team" element={<ProtectedRoute roles={["broker_admin"]}><Team /></ProtectedRoute>} />
              <Route path="analytics" element={<ProtectedRoute roles={["broker_admin"]}><Analytics /></ProtectedRoute>} />
              <Route path="plan" element={<ProtectedRoute roles={["broker_admin", "agent", "landlord"]}><Plan /></ProtectedRoute>} />
              <Route path="business" element={<ProtectedRoute roles={["broker_admin"]}><BusinessProfile /></ProtectedRoute>} />
              <Route path="earnings" element={<ProtectedRoute roles={["agent", "broker_admin", "landlord"]}><EarningsByRole /></ProtectedRoute>} />
              <Route path="referral" element={<ProtectedRoute roles={["agent", "broker_admin"]}><Referral /></ProtectedRoute>} />
              <Route path="profile" element={<Profile />} />
              <Route path="help" element={<div><h1 className="text-2xl font-bold font-display">Help & Support</h1><p className="text-gray-600 mt-2">Email <a className="text-[#F97316]" href="mailto:help@orello.in">help@orello.in</a>. We reply within 4 hours on weekdays.</p></div>} />
              <Route path="rental/:rid" element={<RentalDetail />} />
              <Route path="settings" element={<ProtectedRoute><Settings /></ProtectedRoute>} />
            </Route>

<Route path="/upgrade-to-broker" element={<ProtectedRoute><UpgradeToBroker /></ProtectedRoute>} />
<Route path="/tenant/:slug" element={<TenantProfile />} />
<Route path="/u/:slug" element={<PublicProfile />} />
<Route path="/terms" element={<Terms />} />
<Route path="/privacy" element={<Privacy />} />
            <Route path="/payment/status" element={<PaymentStatus />} />
            <Route path="/admin/*" element={<AdminApp />} />
<Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
          <ChatbotWidget />
        </BrowserRouter>
        </EditModeProvider>
      </AuthProvider>
    </I18nProvider>
    </ThemeProvider>
  );
}

export default App;